import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../../shared/api/errors';
import { FactList, FactRow } from '../../../shared/ui/fact-list';
import { FieldLabel } from '../../../shared/ui/field-label';
import { Input } from '../../../shared/ui/input';
import { Banner } from '../../../shared/ui/layout';
import { Modal } from '../../../shared/ui/modal';
import { Note } from '../../../shared/ui/note';
import { listIpRanges, subnetCount } from '../../../entities/ip-range';
import { activityLine, sourceSecrets, type TSource } from '../../../entities/source';
import type { ISourceScheme } from '../use-source-scheme';

interface IStopCheckingProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: TSource;
  scheme: ISourceScheme;
}

export const StopChecking = ({ open, onOpenChange, source, scheme }: IStopCheckingProps) => {
  const [typed, setTyped] = useState('');

  const secrets = useQuery({
    queryKey: ['source-secrets', source.id],
    queryFn: () => sourceSecrets(source.id),
    enabled: open,
  });

  const filter = scheme.methods.find((method) => method.method === 'ip_allowlist') ?? null;

  const ranges = useQuery({
    queryKey: ['ip-ranges'],
    queryFn: listIpRanges,
    enabled: open && filter !== null && filter.useProviderRanges,
  });

  const losing = (secrets.data ?? []).filter(
    (secret) => secret.authMethodId === scheme.hmacMethodId,
  ).length;

  const published =
    filter?.useProviderRanges && filter.providerRangesKey
      ? (ranges.data?.find((row) => row.key === filter.providerRangesKey)?.cidrCount ?? null)
      : 0;
  const subnets =
    filter === null || published === null ? null : (filter.manualCidrs?.length ?? 0) + published;

  const rate = activityLine('rate', source.activity);

  const close = () => {
    setTyped('');
    onOpenChange(false);
  };

  const stop = () =>
    scheme.hmacMethodId &&
    scheme.remove.mutate(scheme.hmacMethodId, {
      onSuccess: () => {
        setTyped('');
        onOpenChange(false);
      },
    });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Stop checking signatures?"
      description={`${source.name} would accept every request that reaches its address, from anyone who knows it.`}
      submit={{
        label: 'Stop checking',
        onSubmit: stop,
        disabled: typed.trim() !== source.name || scheme.remove.isPending,
        waiting: scheme.remove.isPending,
        waitingLabel: 'Stopping',
      }}
    >
      <FactList>
        {scheme.senderName && (
          <FactRow label={`The ${scheme.senderName} scheme`}>no longer used</FactRow>
        )}
        {losing > 0 && (
          <FactRow label={losing === 1 ? 'The secret' : `The ${losing} secrets`} tone="danger">
            deleted, not kept
          </FactRow>
        )}
        {filter !== null && (
          <FactRow label="Sender addresses" tone="success">
            {subnets === null ? 'still filtered' : `still filtered · ${subnetCount(subnets)}`}
          </FactRow>
        )}
        {rate && <FactRow label="Requests arriving now">{rate}</FactRow>}
      </FactList>

      <Note className="mt-3">
        The address alone is not a secret. It travels in configuration, in logs and in screenshots.
      </Note>

      <div className="mt-4">
        <FieldLabel htmlFor="stop-confirm">Type the source name to confirm</FieldLabel>
        <Input
          id="stop-confirm"
          mono
          autoComplete="off"
          placeholder={source.name}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>

      {scheme.remove.isError && (
        <div className="mt-3">
          <Banner
            detail={scheme.remove.error instanceof ApiError ? scheme.remove.error.code : undefined}
          >
            {scheme.remove.error.message}
          </Banner>
        </div>
      )}
    </Modal>
  );
};
